import { motion, useReducedMotion } from 'framer-motion'
import { ArrowUp } from 'lucide-react'
import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react'
import { formatCredits } from '~/entities/subscription/plans'
import * as m from '~/paraglide/messages'
import { ENTER_EASING } from '~/shared/lib/motion'
import { cn } from '~/shared/lib/utils'

const PIN_PATH =
  'M 30,4 C 14,4 4,15 4,28 C 4,40 16,55 24,65 C 27,69 28,71 30,74 C 32,71 33,69 36,65 C 44,55 56,40 56,28 C 56,15 46,4 30,4 Z'

export function FlowAvatar({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 60 78" aria-hidden="true" className={cn('h-7 w-6 flex-none', className)}>
      <path d={PIN_PATH} fill="var(--mark)" />
      <circle cx="22" cy="27" r="4.4" fill="var(--mark-eye)" />
      <circle cx="38" cy="27" r="4.4" fill="var(--mark-eye)" />
    </svg>
  )
}

function useEnter() {
  const reduced = useReducedMotion()
  return reduced
    ? { initial: false as const }
    : {
        initial: { opacity: 0, y: 10 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.25, ease: ENTER_EASING },
      }
}

export function AssistantMessage({ children }: { children: ReactNode }) {
  const enter = useEnter()
  return (
    <motion.div {...enter} className="flex gap-3">
      <span className="flex size-7 flex-none items-center justify-center rounded-full border border-border bg-muted">
        <FlowAvatar className="h-[18px] w-[14px]" />
      </span>
      <div className="min-w-0 flex-1 pt-0.5 text-[15.5px] text-foreground leading-6">
        {children}
      </div>
    </motion.div>
  )
}

export function UserMessage({ text }: { text: string }) {
  const enter = useEnter()
  return (
    <motion.div {...enter} className="flex justify-end">
      <p className="max-w-[80%] whitespace-pre-wrap rounded-[20px] rounded-br-md bg-chat-pill px-4 py-2.5 font-medium text-[15px] text-chat-pill-ink leading-[22px] shadow-[0_10px_30px_-12px_rgb(0_0_0/0.6)]">
        {text}
      </p>
    </motion.div>
  )
}

export function ThinkingDots() {
  return (
    <AssistantMessage>
      <span className="inline-flex gap-1.5 pt-2.5" role="status" aria-label={m.aiFlowThinking()}>
        {[0, 150, 300].map((delay) => (
          <i
            key={delay}
            className="ai-flow-dot size-1.5 rounded-full bg-brand"
            style={{ animationDelay: `${delay}ms` }}
          />
        ))}
      </span>
    </AssistantMessage>
  )
}

interface ChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  tone?: 'default' | 'go' | 'quiet'
  price?: number | null
}

/** A tappable answer under the latest assistant message, with its price when it costs. */
export function Chip({ tone = 'default', price, className, children, ...props }: ChipProps) {
  return (
    <button
      type="button"
      className={cn(
        'inline-flex h-8 items-center gap-1.5 rounded-full border px-3 font-medium text-[13px] text-foreground outline-none backdrop-blur-md transition-colors duration-200 hover:border-brand-strong hover:bg-brand-strong hover:text-brand-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
        tone === 'go' && 'border-brand/60 bg-brand-subtle',
        tone === 'default' && 'border-border bg-muted',
        tone === 'quiet' && 'border-transparent bg-transparent text-muted-foreground',
        className,
      )}
      {...props}
    >
      {children}
      {price != null && (
        <span className="tabular-nums opacity-75">
          {price > 0 ? m.aiFlowPrice({ credits: formatCredits(price) }) : m.aiFlowIncluded()}
        </span>
      )}
    </button>
  )
}

export function Chips({ children }: { children: ReactNode }) {
  return <div className="mt-2.5 flex flex-wrap gap-1.5">{children}</div>
}

interface ChatInputProps {
  value: string
  onChange: (value: string) => void
  onSubmit: () => void
  placeholder: string
  disabled?: boolean
  canSubmit?: boolean
  submitLabel: string
  actions?: ReactNode
  inputRef?: Ref<HTMLInputElement>
}

/** The white pill input under the chat, framed by a slowly turning coral and amber ring. */
export function ChatInput({
  value,
  onChange,
  onSubmit,
  placeholder,
  disabled,
  canSubmit = true,
  submitLabel,
  actions,
  inputRef,
}: ChatInputProps) {
  return (
    <div className="relative">
      <div aria-hidden className="ai-flow-ring absolute inset-0 rounded-full opacity-45 blur-xl" />
      <div className="ai-flow-ring relative rounded-full p-[1.5px]">
        <form
          className="flex h-[60px] items-center gap-2 rounded-full bg-chat-field pr-2 pl-6 text-chat-field-ink"
          onSubmit={(event) => {
            event.preventDefault()
            if (canSubmit && !disabled && value.trim()) onSubmit()
          }}
        >
          <input
            ref={inputRef}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            placeholder={placeholder}
            aria-label={placeholder}
            disabled={disabled}
            className="min-w-0 flex-1 bg-transparent text-base text-chat-field-ink outline-none placeholder:text-chat-field-ink/50 disabled:opacity-60"
          />
          {actions}
          <button
            type="submit"
            aria-label={submitLabel}
            title={submitLabel}
            disabled={disabled || !canSubmit || !value.trim()}
            className="flex size-11 flex-none items-center justify-center rounded-full bg-chat-send text-chat-send-ink outline-none transition-[opacity,transform] duration-200 focus-visible:ring-2 focus-visible:ring-chat-coral active:scale-95 disabled:opacity-35"
          >
            <ArrowUp className="size-5" strokeWidth={2.5} />
          </button>
        </form>
      </div>
    </div>
  )
}

export function InputAction({
  label,
  onClick,
  children,
}: {
  label: string
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className="flex size-10 flex-none items-center justify-center rounded-full text-chat-field-ink/55 outline-none transition-colors hover:bg-chat-field-ink/5 hover:text-chat-field-ink focus-visible:ring-2 focus-visible:ring-chat-coral"
    >
      {children}
    </button>
  )
}

/** A rounded glass surface for the cards inside the chat. */
export function GlassCard({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'mt-3 rounded-2xl border border-border bg-card p-3 backdrop-blur-xl',
        className,
      )}
    >
      {children}
    </div>
  )
}
